import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import { format } from "date-fns";

const BRAND_LABELS = {
  zona: "866 · Support/Pitch",
  concierge_demo: "786 · Concierge Demo",
  venue_demo: "Venue Demo (outbound)",
};

const VoiceCallDetail = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  // The audio object itself stays private in storage; playback goes through
  // a signed URL that expires, so it's fetched fresh on open rather than
  // stored on the call record.
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);

  const fetchCall = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/voice-calls/${callId}`);
      setCall(data?.data || null);
    } catch (err) {
      console.error("Failed to fetch call:", err);
      setCall(null);
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => {
    fetchCall();
  }, [fetchCall]);

  useEffect(() => {
    if (!call?.audio_path) return;
    (async () => {
      try {
        setAudioLoading(true);
        const { data } = await api.get(`/admin/voice-calls/${callId}/audio`);
        setAudioUrl(data?.url || null);
      } catch (err) {
        console.error("Failed to sign audio URL:", err);
      } finally {
        setAudioLoading(false);
      }
    })();
  }, [call?.audio_path, callId]);

  const formatDate = (d) => {
    if (!d) return "—";
    try { return format(new Date(d), "MMM d, yyyy h:mm:ss a"); } catch { return "—"; }
  };

  // Name the agent by how it introduced itself, so each call keeps the name it was answered with.
  const agentName = (() => {
    const greeting = (call?.turns || []).find((t) => t.role !== "caller");
    const m = greeting && /\b(?:I'm|this is)\s+(Daniel|Dan|Jacqueline|Jen)\b/i.exec(greeting.text || "");
    return m ? m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase() : "Agent";
  })();

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24, maxWidth: 820 }}>
          <button type="button" onClick={() => navigate("/voice-calls")} style={backBtn}>← All calls</button>

          {loading ? (
            <p style={{ color: "rgba(255,255,255,0.6)", marginTop: 24 }}>Loading…</p>
          ) : !call ? (
            <p style={{ color: "rgba(255,255,255,0.6)", marginTop: 24 }}>Call not found.</p>
          ) : (
            <>
              <div style={{ width: "100%", marginTop: 16, marginBottom: 20 }}>
                <h1 style={{ margin: 0, color: "#fff", fontSize: 20 }}>{BRAND_LABELS[call.brand] || call.brand}</h1>
                <p style={{ margin: "6px 0 0", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
                  {call.caller_number ? `From ${call.caller_number} · ` : ""}{formatDate(call.started)}
                </p>
                <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.35)", fontSize: 12 }}>{call.call_id}</p>
              </div>

              {/* .page-content centers its children, so without an explicit width this box
                  shrank to fit and the width:100% player inside collapsed to nothing. */}
              <div style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
                {call.audio_path ? (
                  audioLoading ? (
                    <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, margin: 0 }}>Loading audio…</p>
                  ) : audioUrl ? (
                    <audio controls src={audioUrl} style={{ width: "100%" }} />
                  ) : (
                    <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, margin: 0 }}>Couldn't load audio.</p>
                  )
                ) : (
                  <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, margin: 0 }}>No audio captured on this call.</p>
                )}
              </div>

              <h2 style={{ alignSelf: "flex-start", color: "#fff", fontSize: 14, textTransform: "uppercase", letterSpacing: 1, opacity: 0.6, marginBottom: 10 }}>Transcript</h2>
              {(call.turns || []).length === 0 ? (
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
                  No turns captured — the caller's side never reached transcription on this call.
                </p>
              ) : (
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
                  {call.turns.map((t, i) => (
                    <div
                      key={i}
                      style={{
                        alignSelf: t.role === "caller" ? "flex-start" : "flex-end",
                        maxWidth: "78%",
                        background: t.role === "caller" ? "rgba(255,255,255,0.06)" : "linear-gradient(135deg,#0A3FD6,#7229F5)",
                        color: "#fff",
                        borderRadius: 12,
                        padding: "10px 14px",
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    >
                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, opacity: 0.6, marginBottom: 4 }}>
                        {t.role === "caller" ? "Caller" : agentName}
                      </div>
                      {t.text}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </article>
      </div>
    </main>
  );
};

const backBtn = {
  alignSelf: "flex-start",
  background: "transparent",
  border: "none",
  color: "rgba(255,255,255,0.6)",
  fontSize: 13,
  cursor: "pointer",
  padding: 0,
};

export default VoiceCallDetail;
